import { loadYouTubeApi, ytVideoId } from './youtube.js';

// Homepage interactivity (layout-first → behavior pass).
//
// - Play/pause buttons delegate to the window.GLZ.player facade (real audio player
//   lands in the player island; until then these are no-ops via the facade stub).
// - The live-studio video carousel is hydrated with YouTube IFrame players, mirroring
//   video-carousel.component.ts (cue each slide's embed URL so the thumbnail shows).
//
// Read-more (GLZ university) and the GLGLZ playlist tabs are handled declaratively with
// Alpine in the Razor partials, so they aren't repeated here.

function wirePlayButtons() {
  document.addEventListener('click', e => {
    const playBtn = e.target.closest('[data-play-btn]');
    const queueBtn = e.target.closest('[data-queue-btn]');
    const btn = playBtn || queueBtn;
    if (!btn) return;
    e.preventDefault();
    const trackId = btn.getAttribute('data-track-id');
    const player = window.GLZ && window.GLZ.player;
    if (!player || !trackId) return;
    if (playBtn) player.playTrack(trackId);
    else player.addToQueue(trackId);
  });
}

// Digital stations (GLGLZ home) are live streaming channels keyed by a GUID, not tracks —
// they resolve through /playerv2/GetDigitalStation (channelUrl live stream), so they use their
// own button convention instead of the integer track-id path handled by wirePlayButtons.
function wireStationButtons() {
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-station-btn]');
    if (!btn) return;
    e.preventDefault();
    const id = btn.getAttribute('data-station-id');
    const rootId = btn.getAttribute('data-station-root');
    const player = window.GLZ && window.GLZ.player;
    if (!player || !id || !player.playStation) return;
    player.playStation(id, rootId, {
      title: btn.getAttribute('data-station-title') || '',
      desc: btn.getAttribute('data-station-sub') || ''
    });
  });
}

// Each `.yt-player` holder owns one YT.Player. We keep the ref on the element so the
// overlay play button can drive it — mirroring video-carousel.component.ts, which stored
// `this.players[index]` and called `playVideo()` from togglePlay(). We hydrate EVERY
// holder up front (real slides AND slick clones), exactly like prod: the player must be
// created and primed before the click, or a later programmatic playVideo() (fired outside
// the user gesture) is blocked by the browser autoplay policy.
function hydrateYtPlayer(holder) {
  if (holder._ytReady !== undefined) return; // already hydrating/hydrated
  holder._ytReady = false;
  holder._pendingPlay = false;
  holder.dataset.ytInit = '1';
  // data-yt-url is the /embed/<id> form. YT.Player needs the id via the `videoId`
  // option: passing it builds the iframe with the embed src (so the POSTER frame shows)
  // and cues the video so a gesture-driven playVideo() actually plays. The old code called
  // loadVideoByUrl(embedUrl), which silently no-ops for /embed/ URLs → empty iframe, no poster.
  const videoId = ytVideoId(holder.getAttribute('data-yt-url'));
  loadYouTubeApi().then(() => {
    // eslint-disable-next-line no-undef
    const player = new YT.Player(holder, {
      videoId: videoId || undefined,
      playerVars: { autoplay: 0, controls: 1 },
      events: {
        onReady: () => {
          // The cued videoId already shows the poster frame; nothing else to prime.
          // YT.Player replaces the holder div with an iframe; re-resolve and stash the ref.
          const node = player.getIframe ? player.getIframe() : holder;
          node._ytPlayer = player;
          node._ytReady = true;
          // A click during priming may have flagged either the original div or the iframe.
          if ((node._pendingPlay || holder._pendingPlay) && player.playVideo) player.playVideo();
        }
      }
    });
  });
}

function initVideoPlayers() {
  document.querySelectorAll('.yt-player[data-yt-url]:not([data-yt-init])')
    .forEach(el => hydrateYtPlayer(el));
}

// Overlay play button → start the slide's YouTube video inline (mirror of togglePlay()):
// pause the site audio, mark the item playing (hides the overlay, as Angular's *ngIf did),
// then play the slide's already-primed player synchronously inside the click gesture.
function playVideoItem(item) {
  const node = item.querySelector('iframe') || item.querySelector('.yt-player[data-yt-url]');
  if (!node) return;
  const audio = window.GLZ && window.GLZ.player;
  if (audio && audio.pause) audio.pause();
  item.classList.add('isPlaying');
  const overlay = item.querySelector('.generalPlayBtnPlayer');
  if (overlay) overlay.style.display = 'none';
  if (node._ytReady && node._ytPlayer && node._ytPlayer.playVideo) node._ytPlayer.playVideo();
  else node._pendingPlay = true; // play as soon as the player finishes priming
}

function wireVideoPlayButtons() {
  document.addEventListener('click', e => {
    const btn = e.target.closest('[data-yt-play]');
    if (!btn) return;
    const item = btn.closest('.item');
    if (!item) return;
    e.preventDefault();
    playVideoItem(item);
  });
}

// Reflect playback on the digital-station buttons: the currently-playing station swaps its
// play icon for a pause icon (and gets .isPlaying), all others stay on play. Driven by the
// shared player state, mirroring the legacy digital-stations.component isPlaying toggle.
function reflectStationState() {
  const buttons = document.querySelectorAll('[data-station-btn]');
  if (!buttons.length) return;
  const player = window.GLZ && window.GLZ.player;
  if (!player || !player.onState) return;
  player.onState(s => {
    const activeId = s.playing && s.current ? String(s.current.id) : null;
    buttons.forEach(btn => {
      const on = activeId !== null && btn.getAttribute('data-station-id') === activeId;
      const img = btn.querySelector('img');
      if (img) img.src = on ? '/img/pause.png' : '/img/play.png';
      btn.classList.toggle('isPlaying', on);
    });
  });
}

// Called after carousels are built (clones exist), so clone-filtering works.
export function initHome() {
  wirePlayButtons();
  wireStationButtons();
  reflectStationState();
  if (document.querySelector('.yt-player[data-yt-url]')) {
    wireVideoPlayButtons();
    initVideoPlayers();
  }
}
